import { Component, Host, State, h } from "@stencil/core";
import {
  AppConfigService,
  ThemeService,
  TranslateService,
} from "../../services";
import { IThemeOptions } from "../../models";
import { IScheduleEntry } from "@auvious/utils";

interface IState {
  workHoursVisible?: boolean;
}

const DEFAULT_STATE: IState = {
  workHoursVisible: false,
};

@Component({
  tag: "app-work-hours",
  styleUrl: "app-work-hours.scss",
  shadow: true,
})
export class AppWorkHours {
  @State() themeOptions: IThemeOptions;
  @State() state: IState = { ...DEFAULT_STATE };

  private weekDaysMap = {
    1: "Sun",
    2: "Mon",
    3: "Tue",
    4: "Wed",
    5: "Thu",
    6: "Fri",
    7: "Sat",
  };

  constructor() {
    ThemeService.optionsAvailable().subscribe((o) => (this.themeOptions = o));
  }

  private setState(state: IState) {
    this.state = { ...this.state, ...state };
  }

  private toggleWorkingHours() {
    this.setState({
      workHoursVisible: !this.state.workHoursVisible,
    });
  }

  private renderSchedule() {
    return (
      <div>
        {AppConfigService.schedule?.schedule?.open?.length > 0 && (
          <div role="group">
            <b class="work-hours-title">
              <av-translate role="heading">Open</av-translate>
            </b>
            {AppConfigService.schedule?.schedule?.open.map((s) =>
              this.renderScheduleEntry(s)
            )}
          </div>
        )}
        {AppConfigService.schedule?.schedule?.closed?.length > 0 && (
          <div role="group">
            <b class="work-hours-title work-hours-title-air">
              <av-translate role="heading">Closed</av-translate>
            </b>
            {AppConfigService.schedule?.schedule?.closed.map((s) =>
              this.renderScheduleEntry(s)
            )}
          </div>
        )}
      </div>
    );
  }

  private renderScheduleEntry(entry: IScheduleEntry) {
    return [
      <div role="group" class="work-hours-item">
        <div class="work-hours-item-entry">
          {entry.weekdays && (
            <div class="work-hours-item-day">
              {this.renderWeekDay(entry.weekdays)}
            </div>
          )}
          {!entry.weekdays && !entry.date && (
            <div class="work-hours-item-day">
              <span class="pill">
                <av-translate>Rest of the days</av-translate>
              </span>
            </div>
          )}
          {entry.date && (
            <div class="work-hours-item-date">
              {this.renderDate(entry.date)}
            </div>
          )}
          {entry.time && (
            <div class="work-hours-item-time">
              {this.renderTime(entry.time)}
            </div>
          )}
          {!entry.time && (
            <div class="work-hours-item-time">
              <span class="pill">
                <av-translate>All day</av-translate>
              </span>
            </div>
          )}
        </div>
        {entry.reason && (
          <div class="work-hours-item-reason">{entry.reason}</div>
        )}
      </div>,
    ];
  }

  private renderWeekDay(days: number | number[]) {
    return typeof days === "number" ? (
      <span class="pill">
        <av-translate>{this.numberToDay(days)}</av-translate>
      </span>
    ) : (
      days.map((d) => (
        <span class="pill">
          <av-translate>{this.numberToDay(d)}</av-translate>
        </span>
      ))
    );
  }

  private renderTime(time: string | string[]) {
    return typeof time === "string" ? (
      <span class="pill">{time}</span>
    ) : (
      time.map((t) => <span class="pill">{t}</span>)
    );
  }

  private renderDate(date: string | string[]) {
    return typeof date === "string" ? (
      <span class="pill">{date}</span>
    ) : (
      date.map((t) => <span class="pill">{t}</span>)
    );
  }

  private numberToDay(number) {
    return this.weekDaysMap[number] || "All";
  }

  render() {
    return (
      <Host>
        {AppConfigService.hasSchedule && (
          <div
            role="group"
            class={{
              "work-hours": true,
              "work-hours-dark": this.themeOptions.darkMode,
            }}
          >
            <av-translate>Working hours</av-translate>
            <button
              aria-label={TranslateService.instant("View working hours")}
              class={{ "work-hours-trigger": true }}
              onClick={this.toggleWorkingHours.bind(this)}
            >
              i
            </button>
            <av-card
              role={this.state.workHoursVisible ? "dialog" : null}
              class="work-hours-card"
              open={this.state.workHoursVisible}
            >
              {AppConfigService.schedule?.timeZone && (
                <av-card-header closeHidden={true}>
                  <av-translate>Timezone</av-translate>
                  {": "}
                  {AppConfigService.schedule?.timeZone}
                </av-card-header>
              )}
              <div slot="card-body">{this.renderSchedule()}</div>
            </av-card>
          </div>
        )}
      </Host>
    );
  }
}
